import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import type { ILocalizedInformation, IProduct, ITheme } from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import WaterLabelCardStyle from './WaterLabelCardStyle.js';

const LABEL_FILE_TYPE = 'svg';

/**
 * Data-driven information listing.
 */

export default class WaterLabelCard extends KompisElement {
	// A product.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated (never used).
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	/**
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${WaterLabelCardStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="container">${this.getCard()}</div>
		`;
	}

	private getCard(): RenderResult {
		return html` <div class="labels">${this.getLabels()}</div> `;
	}

	private getLabels(): RenderResult {
		return this.getLabelUrls().map((url) => html`<img src="${url}" alt="${this.getImageAlt()}" />`);
	}

	/**
	 * Return url to the label SVGs.
	 */
	private getLabelUrls(): string[] {
		if (this.product.content?.complianceLabel?.length) {
			return this.product.content.complianceLabel
				.filter((obj) => obj.fileType === LABEL_FILE_TYPE)
				.map((obj) => obj.url);
		}
		return [];
	}

	/**
	 * Returns corresponding text to the water efficiency class image.
	 */
	private getImageAlt(): string {
		const complianceType = ProductUtility.getProductComplianceType(this.product);

		switch (complianceType) {
			case ProductComplianceTechnicalHeadingTypeEnum.unifiedWaterLabel: {
				const eecObject = this.product.content?.complianceTechnical?.find(
					(obj) => obj.headingType === complianceType
				);
				const { headingText, text } = eecObject ?? {};

				return `${headingText} ${text}`;
			}
			default:
				return '';
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-water-label-card': WaterLabelCard;
	}
}
